import * as React from 'react'
import PropTypes from 'prop-types'
import { useHtmlDir } from '@ds/react-utils'
import {
  alignments as popoverAlignments,
  locations as popoverLocations,
  PopoverAlignment,
  PopoverLocation,
} from '../../../components/Popover'
import { useThemeStyles } from '../../../theming'
import styles from './styles'

export interface PopoverArrowProps {
  /**
   * The alignment of the PopoverArrow.
   */
  alignment?: PopoverAlignment
  /**
   * The background color of the PopoverArrow.
   */
  backgroundColor: string
  /**
   * The borderColor color of the PopoverArrow.
   */
  borderColor: string
  /**
   * The width of the border of the PopoverArrow.
   */
  borderWidth: 1 | 2
  /**
   * Accepts custom data attributes.
   */
  'data-qa'?: string
  /**
   * The height of the PopoverArrow.
   */
  height: number
  /**
   * The preferred location of the PopoverArrow relative to its anchor element.
   */
  location?: PopoverLocation
  /**
   * The offset of the PopoverArrow for 'start', 'end' alignments.
   */
  offset?: number
  /**
   * The positionStyles of the PopoverArrow when not using floating UI pointer middleware.
   */
  pointerPositionStyles?: boolean
  /*
   * The width of the PopoverArrow.
   */
  width: number
}

export function PopoverArrow({
  alignment = 'center',
  backgroundColor,
  borderColor,
  borderWidth = 1,
  'data-qa': dataQa,
  height = 10,
  location = 'above',
  offset = 12,
  pointerPositionStyles = false,
  width = 20,
}: PopoverArrowProps) {
  const textDirection = useHtmlDir()
  const isRTL = textDirection === 'rtl'
  const sx = useThemeStyles(styles, {
    alignment,
    borderWidth,
    height,
    isRTL,
    location,
    offset,
    pointerPositionStyles,
    width,
  })

  return (
    <span css={sx.default.pointer} data-qa={dataQa}>
      <svg height={height} width={width}>
        {/* This polygon element is used to create the border color of the PopoverArrow. */}
        <polygon
          fill={borderColor}
          points={`0,${borderWidth} ${width},${borderWidth} ${width / 2},${height}`}
        />
        {/* This polygon element is used to create the background color of the PopoverArrow. */}
        <polygon
          fill={backgroundColor}
          points={`${borderWidth - 0.5},0 ${width - borderWidth + 0.5},0 ${width / 2},${height + 0.5 - borderWidth * 2}`}
        />
      </svg>
    </span>
  )
}

PopoverArrow.propTypes = {
  alignment: PropTypes.oneOf(popoverAlignments),
  backgroundColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  borderWidth: PropTypes.oneOf([1, 2]),
  height: PropTypes.number.isRequired,
  location: PropTypes.oneOf(popoverLocations),
  offset: PropTypes.number,
  pointerPositionStyles: PropTypes.bool,
  width: PropTypes.number.isRequired,
}
